import axios from 'axios';
import { getToken, getRefreshToken, setTokenPair, clearAuth } from './auth.ts';

// All API calls use relative paths — CloudFront proxies /* to Lambda and injects
// x-tenant-slug from the subdomain. In local dev we derive the slug from the
// hostname (e.g. "tenant1.localhost") or fall back to VITE_TENANT_SLUG.
function resolveTenantSlug(): string {
  const envSlug = import.meta.env.VITE_TENANT_SLUG as string | undefined;
  if (envSlug) return envSlug;
  const host = window.location.hostname; // e.g. "tenant1.localhost" or "tenant1.portal.llt-map.justbit.click"
  const parts = host.split('.');
  if (parts.length >= 2 && parts[0] !== 'localhost') return parts[0];
  return 'tenant1'; // last-resort dev fallback
}

export const api = axios.create({
  baseURL: '',
  headers: { 'Content-Type': 'application/json' },
});

// ─── Request interceptor — inject Bearer token + tenant slug ──────────────────
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['x-tenant-slug'] = resolveTenantSlug();
  return config;
});

// ─── Response interceptor — silent token refresh on 401 ──────────────────────
let isRefreshing = false;
let pendingQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function flushQueue(token: string | null, err: unknown) {
  pendingQueue.forEach((p) => (token ? p.resolve(token) : p.reject(err)));
  pendingQueue = [];
}

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    // Only attempt refresh for 401 errors that haven't been retried yet
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      clearAuth();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue concurrent requests until the ongoing refresh completes
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: (token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          },
          reject,
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const res = await axios.post('/customer/auth/refresh', { refreshToken });
      const pair = res.data as {
        accessToken: string;
        refreshToken: string;
        expiresIn: number;
        customerId: string;
      };
      setTokenPair(pair);
      flushQueue(pair.accessToken, null);
      originalRequest.headers.Authorization = `Bearer ${pair.accessToken}`;
      return api(originalRequest);
    } catch (refreshErr) {
      flushQueue(null, refreshErr);
      clearAuth();
      window.location.href = '/login';
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  },
);
